import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import FrequencySelect from "~/components/common/FrequencySelect";
import { useTeam } from "~/contexts/TeamProvider";

import { apiClient } from "../../api/ApiClient";
import FormLabel from "../login/formLabel/FormLabel";
import Select from "../mantine/select/Select";
import Switch from "../switch/Switch";
import Modal from "../ui/modal/Modal";
import styles from "./EditKeywordModal.module.scss";

interface EditKeywordFormData {
  refresh_frequency_override: number | null;
  location_id: number;
  country: string;
  language: string;
  isTrackLocal: boolean;
  ranking_url: string;
}

export interface KeywordAttributes {
  refresh_frequency_override?: number | null;
  location_id?: number;
  country?: string;
  language?: string;
  ranking_url?: string;
}

interface BulkUpdateModalContentProps {
  isOpen: boolean;
  closeModal: () => void;
  setKeywordAttributes: (data: KeywordAttributes) => void;
  isPendingRefreshBulk?: boolean;
}

const BulkUpdateModalContent = ({
  isOpen,
  closeModal,
  setKeywordAttributes,
  isPendingRefreshBulk = false,
}: BulkUpdateModalContentProps) => {
  const { team } = useTeam();
  const [locationOptions, setLocationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { control, handleSubmit, watch, setValue } =
    useForm<EditKeywordFormData>({
      values: {
        refresh_frequency_override: null,
        location_id: 0,
        country: "google.com",
        language: "en",
        isTrackLocal: false,
        ranking_url: "",
      },
    });

  const { mutate: getLocations } = useMutation({
    mutationFn: async (query: string) => {
      const response = await apiClient.locations.searchList({
        fulltext: query,
      });

      const locationOptions =
        response.data.locations?.map((location) => ({
          value: String(location.id),
          label: location.name || "",
        })) || [];
      setLocationOptions(locationOptions);

      return locationOptions;
    },
  });

  const { data: languages } = useQuery({
    queryKey: ["languagesList"],
    queryFn: async () => {
      const response = await apiClient.sites.languagesList();

      return response?.data.languages;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: regions } = useQuery({
    queryKey: ["regionsList"],
    queryFn: async () => {
      const response = await apiClient.sites.regionsList();

      return response?.data.regions;
    },
    staleTime: 1000 * 60 * 30,
  });

  const regionOptions = regions
    ? Object.entries(regions).map(([key, value]) => ({
        value: key,
        label: `${key} (${value.name})`,
      }))
    : [];

  const languageOptions = languages
    ? Object.entries(languages).map(([key, value]) => ({
        value: key,
        label: `${value} (${key})`,
      }))
    : [];

  const onSubmit = (data: EditKeywordFormData) => {
    const payload: KeywordAttributes = {};

    payload.refresh_frequency_override = data.refresh_frequency_override;

    if (data.isTrackLocal && data.location_id) {
      payload.location_id = Number(data.location_id);
    }

    payload.country = data.country;
    payload.language = data.language;

    if (data.ranking_url) {
      payload.ranking_url = data.ranking_url;
    }
    setKeywordAttributes(payload);
    closeModal();
  };

  return (
    <Modal
      isLoading={isPendingRefreshBulk}
      isOpen={isOpen}
      closeModal={closeModal}
      // @ts-expect-error TS2339 non-existing className
      modalLabel={<h3 className={styles.modalTitle}>Update Keywords</h3>}
      buttonLabel="Update Keywords"
      formId="updateKeywordsForm"
    >
      <div>
        <form id="updateKeywordsForm" onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formGrid}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Select
                  label={<FormLabel label="Country" />}
                  data={regionOptions}
                  searchable
                  {...field}
                  value={field.value ? String(field.value) : ""}
                />
              )}
            />

            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Select
                  label={<FormLabel label="Language" />}
                  data={languageOptions}
                  searchable
                  {...field}
                  value={field.value ? String(field.value) : ""}
                />
              )}
            />

            <Controller
              name="refresh_frequency_override"
              control={control}
              render={({ field }) => (
                <FrequencySelect
                  label="Refresh Frequency"
                  defaultSeconds={team?.refresh_frequency}
                  value={field.value}
                  onChange={field.onChange}
                  clearable
                  searchable
                />
              )}
            />

            <div className={styles.formGridSwitch}>
              <Switch
                checked={watch("isTrackLocal")}
                onChange={(e) =>
                  setValue("isTrackLocal", e.currentTarget.checked, {
                    shouldDirty: true,
                  })
                }
              />
              <FormLabel label="Track Local Results" />
            </div>

            {watch("isTrackLocal") && (
              <Controller
                name="location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    label={<FormLabel label="Location" />}
                    data={locationOptions}
                    searchable
                    clearable
                    {...field}
                    onSearchChange={(query) => {
                      getLocations(query);
                    }}
                    value={field.value ? String(field.value) : ""}
                    placeholder="Enter a location or zip code"
                    comboboxProps={{ position: "bottom" }}
                  />
                )}
              />
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default BulkUpdateModalContent;
